/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/QaAddMenuButton.tsx
 * FILE_ID: JOBHUB-QA-ADD-MENU-001
 * PURPOSE: The Build toolbar plus. Products opens the existing add-product popover.
 */
import { useState } from 'react'
import { ListChecks, Package, Plus } from 'lucide-react'
import { ContextMenu, useContextMenu, type ContextMenuItem } from '@quotech/ui'
import { QaAddProductButton } from './QaAddProductControl'
import { useQaStandardChecks } from './useQaStandardChecks'
import type { JobQaTabController } from './useJobQaTabController'

export function QaAddMenuButton({ controller }: { controller: JobQaTabController }) {
  const menu = useContextMenu()
  const checks = useQaStandardChecks(controller)
  const [hint, setHint] = useState<string | null>(null)
  const target = checks.targetId()
  // Reused from: packages/ui/src/qa/QaPhotoDisplayControls.tsx (menu.openAt)
  const open = (event: React.MouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    menu.openAt({
      clientX: rect.left,
      clientY: rect.bottom,
      currentTarget: event.currentTarget,
      preventDefault: () => event.preventDefault(),
    })
  }
  const items: ContextMenuItem[] = [
    {
      id: 'products',
      label: 'Products',
      icon: Package,
      // Reused from: QaAddProductControl.tsx (QaAddProductButton, QaAddProductBody)
      onSelect: () => undefined,
    },
  ]
  return (
    <QaAddProductButton
      controller={controller}
      trigger={(openProducts, isOpen) => (
        <>
          <button
            type="button"
            className="inline-flex min-h-[32px] items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs font-medium"
            aria-label="Add"
            aria-expanded={menu.isOpen || isOpen}
            data-testid="qa-add-menu"
            onClick={open}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
          </button>
          {menu.isOpen ? (
            <ContextMenu
              {...menu.props}
              width={220}
              items={[
                { ...items[0], onSelect: openProducts },
                {
                  id: 'standard-checks',
                  label: 'Standard checks',
                  icon: ListChecks,
                  disabled: !target || checks.loading || checks.failed,
                  hint: !target ? 'Pick a checklist first' : hint ?? undefined,
                  submenu: checks.sets.map((set) => ({
                    id: set.key,
                    label: set.title,
                    hint: `${set.count} checks`,
                    onSelect: () => {
                      setHint(null)
                      void checks.add(set.key).then((result) => {
                        if (result.created.length === 0) setHint('Already in this checklist')
                      })
                    },
                  })),
                },
              ]}
            />
          ) : null}
        </>
      )}
    />
  )
}
